import { ReactNode, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Icon } from '~/components/atomic';
import IconButtonWrapper from '../IconButtonWrapper/IconButtonWrapper';

interface TooltipProps {
  children: ReactNode;
  onClose?: () => void;
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
  width?: number;
}

export default function Tooltip({
  children,
  onClose,
  top,
  left,
  right,
  bottom,
  width,
}: TooltipProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onClose) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  return (
    <Container
      top={top}
      left={left}
      right={right}
      bottom={bottom}
      width={width}
      ref={ref}
    >
      <Content>{children}</Content>
      <ButtonWrapper onClick={onClose} size={16} style={{ width: 16 }}>
        <Icon iconName='Close' size={16} />
      </ButtonWrapper>
    </Container>
  );
}

const Container = styled.div<TooltipProps>`
  ${({ theme, top, left, right, bottom, width }) => css`
    display: flex;
    position: absolute;
    z-index: 1;
    width: ${width ? `${width}px` : '220px'};
    padding: 8px;
    border: 1px solid ${theme.stroke.stroke_1};
    border-radius: 8px;
    background: ${theme.backgroundColor.bg_dark_1};
    gap: 5px;

    ${typeof top === 'number' &&
    css`
      top: ${top ? `${top}px` : 0};
    `};

    ${typeof left === 'number' &&
    css`
      left: ${left ? `${left}px` : 0};
    `};

    ${typeof right === 'number' &&
    css`
      right: ${right ? `${right}px` : 0};
    `};

    ${typeof bottom === 'number' &&
    css`
      bottom: ${bottom ? `${bottom}px` : 0};
    `};
  `}
`;

const Content = styled.div`
  ${({ theme }) => css`
    color: ${theme.content.content_light_1};
    ${theme.fontStyle.text.caption_medium};
    word-break: break-all;
  `}
`;

const ButtonWrapper = styled(IconButtonWrapper)`
  ${({ theme }) => css`
    align-items: flex-start;
    width: fit-content;
    color: ${theme.content.content_light_5};
  `}
`;
